import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChange, SimpleChanges } from "@angular/core"
import { InputInfo, InputStyles } from "../ag-input-field/ag-input-field.component"
import { IconImageService } from "@shared/services/icon-image.service"
import { ImageFilterService } from "@shared/services/image-filter.service"
import { DatePipe } from "@angular/common"
import moment from "moment"

@Component({
  selector: "ag-date-picker",
  templateUrl: "./ag-date-picker.component.html",
  styleUrl: "./ag-date-picker.component.scss",
  standalone: false,
})
export class AgDatePickerComponent implements OnInit, OnChanges {
  selectedMonthYear: Date = new Date()
  weekdays: string[] = ["Mo", "Tu", "We", "Th", "Fr", "Sa", "Su"]
  weeks: Date[][] = []
  selectedStartDate: Date | null = null
  selectedEndDate: Date | null = null
  label: any
  currentDate = "today"
  datepickerShown = false
  insideClick: boolean
  changedDate = false
  selectedStartMonth: Date | null = null
  selectedEndMonth: Date | null = null

  @Input() startDate?: Date = null
  @Input() endDate?: Date = null
  @Input() isDateRange?: boolean = false
  @Input() labelInput?: string = null
  @Input() placeholder?: string = "Select a date"
  @Input() applyButton?: boolean = true
  @Input() placement: Placement //if you want to change the left/right position just give the datepicker a parent div & change its width
  @Input() disableDates: DisableDates = "none"
  @Input() disableStartDate?: Date = null
  @Input() forceWidth?: string = null
  @Input() forceHeight?: string = "48px"
  @Input() useOpenButton: boolean = true
  @Input() triggerOpenDatePicker: boolean = false
  @Input() maxDateRangeSelectable: number = 14
  @Input() type: DatePickerType = "regular"
  @Input() css = {
    backgroundColor: "#f2f2f2",
    label: "grey-label",
  }

  @Input() info: InputInfo = {
    showInfo: false,
    type: "info",
    text: "Info",
  }

  @Output() dateRangeOutput: EventEmitter<Date[]> = new EventEmitter<Date[]>()
  @Output() monthOutput: EventEmitter<string[]> = new EventEmitter<string[]>()

  constructor(
    public iconService: IconImageService,
    private imageFilter: ImageFilterService,
    private datePipe: DatePipe,
  ) {}

  ngOnInit(): void {
    this.checkInitDates()
  }

  // the bound dates can be reset by the parent while this picker stays mounted (clearing a
  // form), so a cleared input has to clear the shown date too - checkInitDates only ever
  // assigns, which would leave the old date on screen.
  // Only a clear is acted on: several callers bind an expression that builds a new Date each
  // change detection run, so reacting to every reported change would overwrite the date the
  // user is picking with the same value the parent already holds.
  ngOnChanges(changes: SimpleChanges): void {
    let cleared = false

    if (this.wasCleared(changes["startDate"])) {
      this.selectedStartDate = null
      this.selectedStartMonth = null
      cleared = true
    }
    if (this.wasCleared(changes["endDate"])) {
      this.selectedEndDate = null
      this.selectedEndMonth = null
      cleared = true
    }

    // the label falls back to the placeholder while changedDate is false
    if (cleared) this.changedDate = this.isDateRange ? !!this.selectedStartDate || !!this.selectedEndDate : !!this.selectedStartDate
  }

  private wasCleared(change: SimpleChange | undefined): boolean {
    if (!change || change.firstChange) return false
    return !change.currentValue && !!change.previousValue
  }

  public openDatePicker(event: Event) {
    event.stopPropagation()
    this.insideClick = true
    this.datepickerShown = !this.datepickerShown
  }

  public closeDatePicker(event: any) {
    this.datepickerShown = event
  }

  public submit() {
    this.changedDate = true
    if (this.isDateRange) this.dateRangeOutput.emit([this.selectedStartDate, this.selectedEndDate])
    else this.dateRangeOutput.emit([this.selectedStartDate])
    this.datepickerShown = false
  }

  public onMonthOutput(month: string[]) {
    if (!this.isDateRange) {
      const date = new Date(month[0])
      this.selectedStartMonth = new Date(date + "-01")
      this.monthOutput.emit([month[0]])
    } else {
      const firstDate = new Date(month[0])
      const lastDate = new Date(month[1])
      this.selectedStartMonth = new Date(firstDate + "-01")
      this.selectedEndMonth = new Date(lastDate + "-01")
      this.monthOutput.emit([month[0], month[1]])
    }
    this.datepickerShown = false
    this.changedDate = true
  }

  public onDateOutput(dates: Date[]) {
    this.selectedStartDate = dates[0]
    if (this.isDateRange) {
      this.selectedEndDate = dates[1]
    }
    this.datepickerShown = false
    this.changedDate = true
    this.submit()
  }

  private checkInitDates() {
    if (this.startDate) {
      this.selectedStartDate = this.startDate
      this.changedDate = true
    }
    if (this.endDate) {
      this.selectedEndDate = this.endDate
    }
  }

  public getLabel(): string {
    if (this.datepickerShown) {
      return "dark-label"
    } else {
      return this.css.label
    }
  }

  public getBackgroundColor(): string {
    if (this.datepickerShown) {
      return "#FFFFFF"
    } else {
      return this.css.backgroundColor
    }
  }

  public checkImageFilter(): string {
    if (this.datepickerShown) {
      return ""
    } else {
      return this.imageFilter.mediumgrey
    }
  }

  public checkBoxShadow(): string {
    if (this.css.backgroundColor === "#FFFFFF" || this.css.backgroundColor === "#ffffff") {
      return "1px 1px 8px rgba(0, 0, 0, 0.2)"
    }
  }
}

export type Placement = "top" | "left" | "right" | "bottom" | "top-left" | "top-right" | "bottom-left" | "bottom-right" | "bottom-just-left" | "zero-right" | "zero-left"

export type DisableDates = "none" | "future" | "past"

export type DatePickerType = "regular" | "month" | "double"
